// Tipos de dominio escritos a mao a partir de supabase/migrations/*.sql.
// Nao ha projeto Supabase real ainda para gerar tipos automaticamente (ver
// nota em supabaseClient.ts) — mantenha isto em sincronia com o schema se as
// migrations mudarem antes de existir um projeto real para gerar tipos.

export type LeadStage =
  | "novo"
  | "tentando_contato"
  | "nao_atendeu"
  | "recusou"
  | "reuniao_agendada"
  | "reuniao_realizada"
  | "cliente_fechado"
  | "nao_fechou";

export const LEAD_STAGES: LeadStage[] = [
  "novo",
  "tentando_contato",
  "nao_atendeu",
  "recusou",
  "reuniao_agendada",
  "reuniao_realizada",
  "cliente_fechado",
  "nao_fechou",
];

export const LEAD_STAGE_LABELS: Record<LeadStage, string> = {
  novo: "Novo",
  tentando_contato: "Tentando contato",
  nao_atendeu: "Não atendeu",
  recusou: "Recusou",
  reuniao_agendada: "Reunião agendada",
  reuniao_realizada: "Reunião realizada",
  cliente_fechado: "Cliente fechado",
  nao_fechou: "Não fechou",
};

// Etapas que fazem parte do caminho principal do funil no kanban (a coluna
// "Arquivado" nao e etapa: e leads.arquivado_em, ver v_funil_atual).
export const KANBAN_COLUMNS: LeadStage[] = [
  "novo",
  "tentando_contato",
  "nao_atendeu",
  "recusou",
  "reuniao_agendada",
  "reuniao_realizada",
  "cliente_fechado",
  "nao_fechou",
];

export type LeadPrioridade = "baixa" | "media" | "alta";

export const LEAD_PRIORIDADES: LeadPrioridade[] = ["baixa", "media", "alta"];

export const LEAD_PRIORIDADE_LABELS: Record<LeadPrioridade, string> = {
  baixa: "Baixa",
  media: "Média",
  alta: "Alta",
};

export type ContactChannel = "ligacao" | "whatsapp";

export type ContactResult =
  | "atendeu"
  | "nao_atendeu"
  | "caixa_postal"
  | "numero_invalido"
  | "pediu_retorno"
  | "recusou";

export const CONTACT_RESULT_LABELS: Record<ContactResult, string> = {
  atendeu: "Atendeu",
  nao_atendeu: "Não atendeu",
  caixa_postal: "Caixa postal",
  numero_invalido: "Número inválido",
  pediu_retorno: "Reagendou",
  recusou: "Recusou",
};

export type MotivoCategoria = "recusa" | "nao_fechou";

export type CanalPermitido = "nenhum" | "somente_ligacao" | "ligacao_e_whatsapp";

export type ImportItemStatus =
  | "importado"
  | "duplicata_suspeita"
  | "bloqueado_nao_contatar"
  | "invalido"
  | "mesclado"
  | "descartado";

export type ImportMatchReason = "telefone" | "nome_cidade" | "nao_contatar";

export interface AppUser {
  id: string;
  nome: string;
  email: string;
  ativo: boolean;
  criado_em: string;
  atualizado_em: string;
}

export interface Motivo {
  id: number;
  categoria: MotivoCategoria;
  codigo: string;
  rotulo: string;
  exige_detalhe: boolean;
  ordem: number;
  ativo: boolean;
  criado_em: string;
}

export interface ImportBatch {
  id: string;
  nome: string;
  origem_fonte: string;
  origem_detalhe: string;
  origem_coletada_em: string;
  observacao: string | null;
  total_linhas: number;
  total_importados: number;
  total_duplicatas: number;
  total_bloqueados: number;
  total_invalidos: number;
  importado_por: string | null;
  importado_em: string;
}

export interface Lead {
  id: string;
  empresa: string;
  ramo: string | null;
  cidade: string | null;
  uf: string | null;
  telefone: string | null;
  pessoa_contato: string | null;
  email: string | null;
  site: string | null;

  telefone_normalizado: string | null;
  empresa_normalizada: string | null;
  cidade_normalizada: string | null;

  origem_fonte: string;
  origem_detalhe: string;
  origem_coletada_em: string;
  batch_id: string | null;

  etapa: LeadStage;
  prioridade: LeadPrioridade;
  responsavel_id: string | null;
  proximo_contato_em: string | null;
  motivo_id: number | null;
  motivo_outro: string | null;

  arquivado_em: string | null;
  arquivado_motivo: string | null;

  tags: string[];

  canal_permitido: CanalPermitido;
  nao_contatar: boolean;

  ultima_tentativa_em: string | null;
  ultima_resposta_em: string | null;
  data_ultima_interacao: string;
  tentativas_total: number;
  tentativas_sem_resposta: number;

  criado_por: string | null;
  criado_em: string;
  atualizado_em: string;

  // v1.1
  reuniao_em: string | null;
  nota_fechamento: string | null;
  /** So preenchido pela integracao automatica do Google Calendar (caminho B,
   * nao implementado no front ainda). Nunca escrito pelo cliente. */
  google_event_id: string | null;
}

// Lead com o nome do responsavel embutido via embed do PostgREST
// (.select("*, responsavel:app_users!responsavel_id(nome)")).
export interface LeadComResponsavel extends Lead {
  responsavel: { nome: string } | null;
  lote: { nome: string } | null;
}

export interface NaoContatar {
  id: string;
  telefone_normalizado: string | null;
  empresa_normalizada: string | null;
  cidade_normalizada: string | null;
  motivo: string;
  origem_lead_id: string | null;
  marcado_por: string | null;
  marcado_em: string;
  revogado_em: string | null;
  revogado_por: string | null;
  revogado_motivo: string | null;
}

export interface ContactAttempt {
  id: string;
  lead_id: string;
  ocorreu_em: string;
  canal: ContactChannel;
  resultado: ContactResult;
  autor_id: string;
  nota: string | null;
  anulado_em: string | null;
  anulado_por: string | null;
  anulado_motivo: string | null;
  criado_em: string;
}

export interface ContactAttemptComAutor extends ContactAttempt {
  autor: { nome: string } | null;
}

export interface LeadStageEvent {
  id: number;
  lead_id: string;
  de: LeadStage | null;
  para: LeadStage;
  motivo_id: number | null;
  motivo_outro: string | null;
  autor_id: string | null;
  ocorreu_em: string;
}

export interface LeadStageEventComAutor extends LeadStageEvent {
  autor: { nome: string } | null;
  motivo: { rotulo: string } | null;
}

export interface ImportItem {
  id: string;
  batch_id: string;
  linha_num: number;
  payload: Record<string, unknown>;
  status: ImportItemStatus;
  lead_id: string | null;
  match_lead_id: string | null;
  motivo_match: ImportMatchReason | null;
  erro: string | null;
  origem_fonte: string | null;
  origem_detalhe: string | null;
  origem_coletada_em: string | null;
  resolvido_por: string | null;
  resolvido_em: string | null;
  criado_em: string;
}

// ---------------------------------------------------------------------------
// Views (todas com security_invoker = true, ver 20260902090500_views.sql)
// ---------------------------------------------------------------------------

export interface VFunilAtual {
  etapa: LeadStage;
  ordem: number;
  total_ativos: number;
  total_arquivados: number;
  total: number;
  total_vencidos: number;
}

export interface VTentativasPeriodo {
  dia: string;
  canal: ContactChannel;
  resultado: ContactResult;
  autor_id: string;
  total: number;
}

export interface VConversaoEtapas {
  ordem: number;
  etapa: LeadStage;
  leads_que_alcancaram: number;
  leads_etapa_anterior: number | null;
  conversao_pct: number | null;
}

export interface VLeadTempoAteReuniao {
  lead_id: string;
  empresa: string;
  cidade: string | null;
  responsavel_id: string | null;
  entrou_em: string;
  reuniao_agendada_em: string;
  tempo_ate_reuniao: string;
  dias_ate_reuniao: number;
}

export interface VTempoAteReuniao {
  leads_com_reuniao: number;
  media_dias: number | null;
  mediana_dias: number | null;
  min_dias: number | null;
  max_dias: number | null;
}

export interface VExportLead {
  id: string;
  empresa: string;
  ramo: string | null;
  cidade: string | null;
  uf: string | null;
  telefone: string | null;
  pessoa_contato: string | null;
  email: string | null;
  site: string | null;
  etapa: LeadStage;
  prioridade: LeadPrioridade;
  responsavel: string | null;
  proximo_contato_em: string | null;
  arquivado_em: string | null;
  motivo: string | null;
  motivo_outro: string | null;
  tags: string;
  canal_permitido: CanalPermitido;
  nao_contatar: boolean;
  origem_fonte: string;
  origem_detalhe: string;
  origem_coletada_em: string;
  lote_origem: string | null;
  tentativas_total: number;
  tentativas_sem_resposta: number;
  ultima_tentativa_em: string | null;
  ultima_resposta_em: string | null;
  data_ultima_interacao: string;
  criado_por: string | null;
  criado_em: string;
  // v1.1
  reuniao_em: string | null;
  nota_fechamento: string | null;
}

// ---------------------------------------------------------------------------
// RPCs (supabase/migrations/20260902090400_rpc.sql)
// ---------------------------------------------------------------------------

export interface ImportarLoteResumo {
  batch_id: string;
  nome: string;
  total_linhas: number;
  total_importados: number;
  total_duplicatas: number;
  total_bloqueados: number;
  total_invalidos: number;
}

export type ResolverDuplicataAcao = "criar_novo" | "mesclar" | "descartar";

export interface ResolverDuplicataResultado {
  item_id: string;
  acao: ResolverDuplicataAcao;
  status: ImportItemStatus;
  lead_id: string | null;
}

export interface MarcarNaoContatarResultado {
  lead_id: string;
  supressao_id: string | null;
  ja_existia: boolean;
}

export interface RevogarNaoContatarResultado {
  supressao_id: string;
  revogado_em: string;
}

export interface ExcluirDadosTitularResultado {
  lead_id: string;
  excluido_em: string;
  tentativas_excluidas: number;
  eventos_excluidos: number;
  itens_import_expurgados: number;
  supressao_preservada: boolean;
  motivo: string;
}

// exportar_dados_titular retorna um blob JSON de forma livre (lead + relacoes);
// nao ha necessidade de tipar campo a campo para so oferecer download.
export type ExportarDadosTitularResultado = Record<string, unknown>;

// ---------------------------------------------------------------------------
// v1.1 — supabase/migrations/20260903100000_v11_scripts_abordagem.sql
// ---------------------------------------------------------------------------

export interface Script {
  id: number;
  ramo: string | null;
  titulo: string;
  conteudo: string;
  canal: ContactChannel | null;
  ativo: boolean;
  ramo_normalizado: string | null;
  criado_por: string | null;
  criado_em: string;
  atualizado_em: string;
}

// ---------------------------------------------------------------------------
// v1.1 — supabase/migrations/20260903100200_v11_adiar_lead.sql (RPC adiar_lead)
// ---------------------------------------------------------------------------

export interface AdiarLeadResultado {
  lead_id: string;
  empresa: string;
  proximo_contato_em: string;
}

// ---------------------------------------------------------------------------
// v1.1 — supabase/migrations/20260903100300_v11_views_parados_score.sql
// ---------------------------------------------------------------------------

export interface VLeadParado {
  lead_id: string;
  empresa: string;
  cidade: string | null;
  ramo: string | null;
  etapa: LeadStage;
  prioridade: LeadPrioridade;
  responsavel_id: string | null;
  proximo_contato_em: string | null;
  ultima_tentativa_em: string | null;
  tentativas_total: number;
  tentativas_sem_resposta: number;
  entrou_na_etapa_em: string;
  ultima_movimentacao_em: string;
  dias_parado: number;
  dias_na_etapa: number;
  dias_atrasado: number | null;
  parado_default: boolean;
}

export type LeadScoreFaixa = "quente" | "morno" | "frio";

export interface LeadScore {
  lead_id: string;
  empresa: string;
  cidade: string | null;
  ramo: string | null;
  etapa: LeadStage;
  prioridade: LeadPrioridade;
  responsavel_id: string | null;
  tentativas_total: number;
  tentativas_sem_resposta: number;
  data_ultima_interacao: string;
  score: number;
  faixa: LeadScoreFaixa;
  /** Explicacao legivel de cada fator que compos o score (heuristica
   * declarada, nao aprendida — ver comentario na migration). Um text[] do
   * Postgres, nao jsonb: supabase-js devolve como string[] comum. */
  componentes: string[];
}

// ---------------------------------------------------------------------------
// v1.1 — supabase/migrations/20260903100500_v11_google_calendar.sql
// (RPC dados_evento_calendar — caminho A, link manual)
// ---------------------------------------------------------------------------

export interface DadosEventoCalendar {
  lead_id: string;
  titulo: string;
  detalhes: string | null;
  local: string | null;
  inicio: string;
  fim: string;
  /** Ja no formato exigido pelo parametro `dates` do template do Google
   * (`YYYYMMDDTHHMMSSZ/YYYYMMDDTHHMMSSZ`) — nao encodar, a barra faz parte
   * do formato. */
  dates: string;
  template_base: string;
}
